import type { RoleKeySegments } from '@/api/_shared/keys';

/**
 * React Query cache key factories for the MCP Appointments API.
 * Tuple shapes must match existing inline keys for invalidation compatibility.
 */

type RoleKeyFirst = string | undefined;

/** Cache keys for resource queries and mutations. */
export const resourceKeys = {
  all: ['resources'] as const,
  lists: () => [...resourceKeys.all, 'list'] as const,
  /** Full list key: `['resources', 'list', ...roleKey, params]` */
  list: (roleKey: RoleKeySegments, params: unknown) =>
    [...resourceKeys.lists(), ...roleKey, params] as const,
  /** Detail key: `['resources', roleKeyFirst, 'get', id]` */
  detail: (roleKeyFirst: RoleKeyFirst, id: string) =>
    [...resourceKeys.all, roleKeyFirst, 'get', id] as const,
  policy: (roleKeyFirst: RoleKeyFirst, id: string) =>
    [...resourceKeys.all, roleKeyFirst, 'policy', id] as const,
  availability: (roleKeyFirst: RoleKeyFirst, id: string, weekday: string) =>
    [...resourceKeys.all, roleKeyFirst, 'availability', id, weekday] as const,
  /** Prefix for invalidating all weekday availability caches for a resource. */
  availabilityAll: (roleKeyFirst: RoleKeyFirst, id: string) =>
    [...resourceKeys.all, roleKeyFirst, 'availability', id] as const,
  exceptions: (roleKeyFirst: RoleKeyFirst, id: string, range: { start?: string; end?: string }) =>
    [...resourceKeys.all, roleKeyFirst, 'exceptions', id, range] as const,
  /** Prefix for invalidating all exception range caches for a resource. */
  exceptionsAll: (roleKeyFirst: RoleKeyFirst, id: string) =>
    [...resourceKeys.all, roleKeyFirst, 'exceptions', id] as const,
  availableSlots: (roleKeyFirst: RoleKeyFirst, id: string, params: unknown) =>
    [...resourceKeys.all, roleKeyFirst, 'available-slots', id, params] as const,
  calendarImportFeeds: (roleKeyFirst: RoleKeyFirst, id: string) =>
    [...resourceKeys.all, roleKeyFirst, 'calendar-import-feeds', id] as const,
  calendarExportFeed: (roleKeyFirst: RoleKeyFirst, id: string, baseUrl: string) =>
    [...resourceKeys.all, roleKeyFirst, 'calendar-export-feed', id, baseUrl] as const,
  calDavFeed: (roleKeyFirst: RoleKeyFirst, id: string) =>
    [...resourceKeys.all, roleKeyFirst, 'caldav-feed', id] as const,
};

/** Cache keys for service queries and mutations. */
export const serviceKeys = {
  all: ['services'] as const,
  lists: () => [...serviceKeys.all, 'list'] as const,
  list: (roleKey: RoleKeySegments, params: unknown) =>
    [...serviceKeys.lists(), ...roleKey, params] as const,
  detail: (roleKeyFirst: RoleKeyFirst, id: string) =>
    [...serviceKeys.all, roleKeyFirst, 'get', id] as const,
  availableSlots: (roleKeyFirst: RoleKeyFirst, id: string, params: unknown) =>
    [...serviceKeys.all, roleKeyFirst, 'available-slots', id, params] as const,
};

/** Cache keys for service-role queries and mutations. */
export const serviceRoleKeys = {
  all: ['service-roles'] as const,
  lists: () => [...serviceRoleKeys.all, 'list'] as const,
  list: (roleKey: RoleKeySegments, params: unknown) =>
    [...serviceRoleKeys.lists(), ...roleKey, params] as const,
  detail: (roleKeyFirst: RoleKeyFirst, id: string) =>
    [...serviceRoleKeys.all, roleKeyFirst, 'get', id] as const,
};

/** Cache keys for appointment queries and mutations. */
export const appointmentKeys = {
  all: ['appointments'] as const,
  /** Prefix of {@link stats} for one role scope; used for invalidation. */
  statsForRole: (roleKey: RoleKeySegments) => [...appointmentKeys.all, 'stats', ...roleKey] as const,
  stats: (roleKey: RoleKeySegments, params: unknown) =>
    [...appointmentKeys.all, 'stats', ...roleKey, params] as const,
  lists: () => [...appointmentKeys.all, 'list'] as const,
  list: (roleKey: RoleKeySegments, params: unknown) =>
    [...appointmentKeys.lists(), ...roleKey, params] as const,
  /** Prefix of {@link list} for one role scope; used for invalidation. */
  listsForRole: (roleKey: RoleKeySegments) => [...appointmentKeys.lists(), ...roleKey] as const,
  /** Prefix of {@link listExternal} for one role scope; used for invalidation. */
  listsExternalForRole: (roleKey: RoleKeySegments) => [...appointmentKeys.all, 'list', 'external', ...roleKey] as const,
  listExternal: (roleKey: RoleKeySegments, params: unknown) =>
    [...appointmentKeys.all, 'list', 'external', ...roleKey, params] as const,
  detail: (roleKeyFirst: RoleKeyFirst, id: string) =>
    [...appointmentKeys.all, roleKeyFirst, 'get', id] as const,
  /** Admin-only delete invalidation key (hardcoded 'admin' segment). */
  adminDetail: (id: string) => [...appointmentKeys.all, 'admin', 'get', id] as const,
  /** Prefix of every reschedule-request list (customer and client); used for invalidation. */
  rescheduleRequestsAll: () => [...appointmentKeys.all, 'reschedule-requests'] as const,
  rescheduleRequests: (params: unknown) =>
    [...appointmentKeys.all, 'reschedule-requests', 'customer', params] as const,
  clientRescheduleRequests: (roleKey: RoleKeySegments, params: unknown) =>
    [...appointmentKeys.all, 'reschedule-requests', 'client', ...roleKey, params] as const,
};
